import type { CmmdCloudProjectLifecyclePreflight } from "@cmmd-forge/contracts";

import {
  DiagnosticsCard,
  DiagnosticsSection,
  StatusPill,
  formatTimestamp,
} from "./DiagnosticsPanelPrimitives";
import type { CloudRuntimeView } from "./DiagnosticsCloudRuntimes.logic";

function EvidenceMetric({
  label,
  value,
  mono = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}) {
  return (
    <div className="min-w-0 border-r border-t border-border/65 px-4 py-3 first:border-t-0 md:[&:nth-child(-n+3)]:border-t-0">
      <div className="text-[11px] font-medium text-muted-foreground">{label}</div>
      <div className={mono ? "mt-1 truncate font-mono text-xs" : "mt-1 truncate text-sm"}>
        {value}
      </div>
    </div>
  );
}

function identityMatches(
  runtime: CloudRuntimeView,
  receipt: CmmdCloudProjectLifecyclePreflight,
): boolean {
  return (
    receipt.projectId === runtime.cmmdProjectId &&
    receipt.runtime?.runtimeId === runtime.runtimeId &&
    String(receipt.organizationId) === runtime.cmmdOrganizationId
  );
}

function LifecycleEvidence({ receipt }: { readonly receipt: CmmdCloudProjectLifecyclePreflight }) {
  const runtime = receipt.runtime;
  const removal = receipt.removal;
  return (
    <div className="grid border-b border-border/65 md:grid-cols-3">
      <EvidenceMetric label="Project" value={String(receipt.projectId)} mono />
      <EvidenceMetric label="Observed" value={formatTimestamp(receipt.observedAt)} />
      <RuntimeEvidence runtime={runtime} />
      <InventoryEvidence inventory={receipt.inventory} />
      <EvidenceMetric
        label="Active removal"
        value={removal ? `${removal.status} · ${removal.phase.replaceAll("_", " ")}` : "None"}
      />
      <EvidenceMetric
        label="Registered"
        value={formatTimestamp(receipt.registration.registeredAt)}
      />
    </div>
  );
}

function RuntimeEvidence({
  runtime,
}: {
  readonly runtime: CmmdCloudProjectLifecyclePreflight["runtime"];
}) {
  return (
    <>
      <RuntimeIdentityEvidence runtime={runtime} />
      <RuntimeStateEvidence runtime={runtime} />
    </>
  );
}

function RuntimeIdentityEvidence({
  runtime,
}: {
  readonly runtime: CmmdCloudProjectLifecyclePreflight["runtime"];
}) {
  return (
    <>
      <EvidenceMetric label="Runtime" value={runtime?.runtimeId ?? "Not provisioned"} mono />
      <EvidenceMetric
        label="Generation"
        value={runtime ? String(runtime.credentialEpoch) : "Not provisioned"}
      />
      <EvidenceMetric
        label="Installed commit"
        value={runtime?.forgeGitCommit ?? "Not reported"}
        mono
      />
    </>
  );
}

function RuntimeStateEvidence({
  runtime,
}: {
  readonly runtime: CmmdCloudProjectLifecyclePreflight["runtime"];
}) {
  return (
    <>
      <EvidenceMetric label="Lifecycle" value={runtime?.status ?? "Registered"} />
      <EvidenceMetric label="Desired state" value={runtime?.desiredState ?? "Not provisioned"} />
      <ServiceEvidence service={runtime?.supervisedService ?? null} />
    </>
  );
}

function ServiceEvidence({
  service,
}: {
  readonly service: NonNullable<CmmdCloudProjectLifecyclePreflight["runtime"]>["supervisedService"];
}) {
  return (
    <>
      <EvidenceMetric label="Supervised service" value={service?.name ?? "Not reported"} />
      <EvidenceMetric label="Service status" value={service?.status ?? "Not reported"} />
      <EvidenceMetric label="Service machine" value={service?.machineId ?? "Not reported"} mono />
      <EvidenceMetric
        label="Last heartbeat"
        value={formatTimestamp(service?.lastHeartbeatAt ?? "")}
      />
    </>
  );
}

function InventoryEvidence({
  inventory,
}: {
  readonly inventory: CmmdCloudProjectLifecyclePreflight["inventory"];
}) {
  return (
    <>
      <EvidenceMetric
        label="Published projects"
        value={inventory ? String(inventory.projectCount) : "Not published"}
      />
      <EvidenceMetric
        label="Published threads"
        value={inventory ? String(inventory.threadCount) : "Not published"}
      />
      <EvidenceMetric
        label="Inventory published"
        value={formatTimestamp(inventory?.publishedAt ?? "")}
      />
      <EvidenceMetric
        label="Provider catalog"
        value={inventory?.providerCatalog ?? "Not published"}
      />
    </>
  );
}

export function DiagnosticsLifecyclePreflight({
  runtime,
  receipt,
  error,
  pending,
}: {
  readonly runtime: CloudRuntimeView;
  readonly receipt: CmmdCloudProjectLifecyclePreflight | undefined;
  readonly error: string | undefined;
  readonly pending: boolean;
}) {
  if (pending) {
    return (
      <DiagnosticsSection
        title="Lifecycle preflight"
        description="Reading persisted CMMD lifecycle evidence…"
      />
    );
  }
  if (!receipt) {
    return (
      <DiagnosticsSection
        title="Lifecycle preflight"
        description={error ?? "CMMD has not returned an authorized receipt for this project."}
      />
    );
  }
  if (!identityMatches(runtime, receipt)) {
    return (
      <DiagnosticsSection
        title="Lifecycle preflight"
        description="Identity mismatch: CMMD returned evidence for a different project, organization, or runtime. Nothing is displayed."
      />
    );
  }
  return (
    <DiagnosticsSection
      title="Lifecycle preflight"
      id="diagnostics-lifecycle"
      description="Persisted CMMD receipt. No live Sprite probe, wake, reconnect, or lifecycle action."
      action={<StatusPill label="Evidence only" tone="muted" />}
    >
      <DiagnosticsCard>
        <LifecycleEvidence receipt={receipt} />
      </DiagnosticsCard>
    </DiagnosticsSection>
  );
}
